// File: src/pages/Admin/GPT_Cockpit.js
import React, { useEffect, useState } from "react";
import axios from "axios";
import config from "../../config/config";

const GPTCockpit = () => {
  const [prompt, setPrompt] = useState("Was ist der Zweck des Matching-Moduls?");
  const [response, setResponse] = useState("");
  const [markdown, setMarkdown] = useState("");
  const [filePath, setFilePath] = useState("src/routes/gptRoutes.js");
  const [folderPath, setFolderPath] = useState("src/routes");
  const [docIndex, setDocIndex] = useState([]);
  const [autodocStatus, setAutodocStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [section, setSection] = useState("autodoc");

  const loadStatus = async () => {
    try {
      const s = await axios.get(`${config.API_BASE_URL}/api/gpt/autodoc/status`);
      setAutodocStatus(s.data);
    } catch (err) {
      console.error("❌ Fehler beim AutoDoc-Status", err);
    }
  };

  const loadIndex = async () => {
    try {
      const res = await axios.get(`${config.API_BASE_URL}/api/gpt/doc/index`);
      setDocIndex(res.data.index || []);
    } catch (err) {
      console.error("❌ Fehler beim Laden der Indexdaten", err);
    }
  };

  useEffect(() => {
    loadStatus();
    loadIndex();
  }, []);

  const runPrompt = async () => {
    setLoading(true);
    setResponse("");
    try {
      const res = await axios.post(`${config.API_BASE_URL}/api/gpt/task`, {
        task: prompt,
        language: "de",
        role: "system"
      });
      setResponse(res.data.result);
    } catch {
      setResponse("❌ Fehler beim GPT-Aufruf");
    }
    setLoading(false);
  };

  const runAutoDoc = async () => {
    setLoading(true);
    setMarkdown("🚀 AutoDoc wird ausgeführt...");
    try {
      const res = await axios.post(`${config.API_BASE_URL}/api/gpt/autodoc/trigger`);
      setMarkdown(`✅ ${res.data.result.updated} Datei(en) aktualisiert.`);
      loadStatus();
      loadIndex();
    } catch {
      setMarkdown("❌ Fehler bei AutoDoc.");
    }
    setLoading(false);
  };

  const runDocFile = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${config.API_BASE_URL}/api/gpt/doc/file`, { filePath });
      setMarkdown(`✅ Datei dokumentiert: ${res.data.file}`);
      loadIndex();
    } catch {
      setMarkdown("❌ Fehler bei Dateidokumentation");
    }
    setLoading(false);
  };

  const runDocFolder = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${config.API_BASE_URL}/api/gpt/doc/folder`, { folder: folderPath });
      setMarkdown(`📂 ${res.data.processed} Datei(en) verarbeitet`);
      loadIndex();
    } catch {
      setMarkdown("❌ Fehler bei Ordner-Dokumentation");
    }
    setLoading(false);
  };

  const updateContext = async () => {
    setLoading(true);
    try {
      await axios.post(`${config.API_BASE_URL}/api/gpt/context/update`);
      alert("✅ Kontext aktualisiert");
    } catch {
      alert("❌ Fehler beim Aktualisieren des Kontextes");
    }
    setLoading(false);
  };

  return (
    <div className="max-w-6xl mx-auto p-6 text-white bg-gray-900 rounded shadow-lg">
      <h1 className="text-3xl font-bold mb-6">🧠 GPT Cockpit</h1>

      {/* Tab-Steuerung */}
      <div className="flex gap-3 mb-6 text-sm">
        <button onClick={() => setSection("autodoc")} className={section === "autodoc" ? "text-blue-400 font-bold" : ""}>📘 AutoDocEngine</button>
        <button onClick={() => setSection("doc")} className={section === "doc" ? "text-blue-400 font-bold" : ""}>📂 Dokumentation</button>
        <button onClick={() => setSection("prompt")} className={section === "prompt" ? "text-blue-400 font-bold" : ""}>💬 GPT-Task</button>
        <button onClick={() => setSection("docs")} className={section === "docs" ? "text-blue-400 font-bold" : ""}>📚 Docs</button>
      </div>

      {section === "autodoc" && (
        <div className="mb-6">
          <h3 className="text-xl font-semibold mb-2">📘 AutoDocEngine</h3>
          <p className="text-sm text-gray-400 mb-2">
            Letzter Lauf: {autodocStatus?.lastRun ? new Date(autodocStatus.lastRun).toLocaleString("de-DE") : "unbekannt"} | Dateien: {autodocStatus?.files || 0}
          </p>
          <button onClick={runAutoDoc} disabled={loading} className="bg-purple-600 hover:bg-purple-700 px-4 py-2 rounded mr-2">Jetzt AutoDoc starten</button>
          <button onClick={updateContext} disabled={loading} className="bg-teal-600 hover:bg-teal-700 px-4 py-2 rounded">Kontext aktualisieren</button>
        </div>
      )}

      {section === "doc" && (
        <>
          <div className="mb-4">
            <label className="block mb-1 font-semibold">📄 Datei dokumentieren</label>
            <input type="text" value={filePath} onChange={(e) => setFilePath(e.target.value)} className="w-full p-2 bg-gray-800 border border-gray-600 rounded" />
            <button onClick={runDocFile} disabled={loading} className="mt-2 bg-blue-600 px-4 py-2 rounded hover:bg-blue-700">Dokumentieren</button>
          </div>
          <div className="mb-6">
            <label className="block mb-1 font-semibold">📂 Ordner dokumentieren</label>
            <input type="text" value={folderPath} onChange={(e) => setFolderPath(e.target.value)} className="w-full p-2 bg-gray-800 border border-gray-600 rounded" />
            <button onClick={runDocFolder} disabled={loading} className="mt-2 bg-green-600 px-4 py-2 rounded hover:bg-green-700">Scannen & Dokumentieren</button>
          </div>
        </>
      )}

      {section === "prompt" && (
        <div className="mb-6">
          <label className="block mb-1 font-semibold">💬 GPT-Eingabe</label>
          <textarea rows={3} value={prompt} onChange={(e) => setPrompt(e.target.value)} className="w-full p-2 bg-gray-800 border border-gray-600 rounded mb-2" />
          <button onClick={runPrompt} disabled={loading} className="bg-blue-500 hover:bg-blue-600 px-4 py-2 rounded">Ausführen</button>
          {response && (
            <pre className="mt-4 bg-gray-800 p-4 rounded border border-gray-700 whitespace-pre-wrap text-sm">
              {response}
            </pre>
          )}
        </div>
      )}

      {section === "docs" && (
        <div>
          <h3 className="text-xl font-semibold mb-2">📚 Dokumentierte Dateien</h3>
          <ul className="list-disc list-inside text-blue-300 text-sm">
            {docIndex.length > 0 ? (
              docIndex.map((item, idx) => (
                <li key={idx}>
                  <a href={item.path} target="_blank" rel="noreferrer" className="hover:underline">{item.file}</a>
                </li>
              ))
            ) : (
              <li>Keine Dokumentation gefunden.</li>
            )}
          </ul>
        </div>
      )}

      {markdown && (
        <div className="mt-6 bg-gray-800 p-4 rounded border border-gray-700 text-sm whitespace-pre-wrap">
          {markdown}
        </div>
      )}
    </div>
  );
};

export default GPTCockpit;
